import { useMemo, useState, type ReactNode } from 'react';

export interface Colonne<T> {
  cle: string;
  entete: string;
  /** Aligné à droite, en chasse fixe : réservé aux montants et quantités. */
  numerique?: boolean;
  rendu: (ligne: T) => ReactNode;
  /**
   * Valeur en TEXTE BRUT — pour l'export PDF, qui ne sait pas rendre un
   * `<Badge>` ou un `<Montant>`. Facultative : sans elle, la colonne est
   * simplement absente du PDF plutôt que d'y montrer `[object Object]`.
   */
  texte?: (ligne: T) => string;
  /**
   * Valeur COMPARABLE — pour le tri. Facultative : sans elle, l'en-tête
   * n'est pas cliquable. Une colonne d'actions n'a rien à comparer, une
   * colonne de badges a souvent besoin d'un ordre qui n'est pas celui du
   * texte affiché (ex. trier le stock par quantité, pas par le libellé
   * « Rupture »).
   */
  valeur?: (ligne: T) => string | number;
}

interface Tri {
  cle: string;
  sens: 'asc' | 'desc';
}

/**
 * Tableau de données — alternat léger, colonnes numériques à droite.
 *
 * [vide] est OBLIGATOIRE, et c'est délibéré. Un tableau sans lignes et sans
 * explication laisse croire à une panne ; il faut dire s'il n'y a réellement
 * rien, ou si un filtre a tout écarté. Rendre ce texte facultatif reviendrait
 * à autoriser l'écran muet.
 */
export function DataTable<T>({
  colonnes,
  lignes,
  cleDe,
  vide,
  triParDefaut,
}: {
  colonnes: Colonne<T>[];
  lignes: T[];
  cleDe: (ligne: T) => string;
  vide: string;
  /**
   * Colonne triée à l'ouverture — doit nommer une colonne dotée de
   * `valeur`, sinon elle est ignorée (le tableau reste dans l'ordre reçu).
   */
  triParDefaut?: { cle: string; sens?: 'asc' | 'desc' };
}): ReactNode {
  const [tri, setTri] = useState<Tri | null>(
    triParDefaut === undefined
      ? null
      : { cle: triParDefaut.cle, sens: triParDefaut.sens ?? 'asc' },
  );

  const colonneTriee = tri === null ? undefined : colonnes.find((c) => c.cle === tri.cle);

  const lignesTriees = useMemo(() => {
    if (tri === null || colonneTriee?.valeur === undefined) return lignes;
    const valeurDe = colonneTriee.valeur;
    const sens = tri.sens === 'asc' ? 1 : -1;
    // Copie : jamais de `.sort()` sur le tableau reçu en props, qui
    // appartient à l'appelant.
    return [...lignes].sort((a, b) => {
      const va = valeurDe(a);
      const vb = valeurDe(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sens;
      return String(va).localeCompare(String(vb)) * sens;
    });
  }, [lignes, tri, colonneTriee]);

  if (lignes.length === 0) {
    return <p className="table-vide">{vide}</p>;
  }

  function basculerTri(colonne: Colonne<T>): void {
    if (colonne.valeur === undefined) return;
    setTri((actuel) => {
      if (actuel?.cle !== colonne.cle) return { cle: colonne.cle, sens: 'asc' };
      return { cle: colonne.cle, sens: actuel.sens === 'asc' ? 'desc' : 'asc' };
    });
  }

  return (
    // Sans ce conteneur, un `<td>` qui réclame plus de place que l'écran
    // élargit toute la grille parente (`.contenu` a `min-width: 0` pour ça)
    // et c'est LA PAGE ENTIÈRE qui défile latéralement sur un téléphone —
    // au lieu du seul tableau, qui a son propre défilement contenu.
    <div className="tableau-defilant">
      <table className="table">
        <thead>
          <tr>
            {colonnes.map((colonne) => {
              const triable = colonne.valeur !== undefined;
              const sensActif = tri?.cle === colonne.cle ? tri.sens : null;
              return (
                <th
                  key={colonne.cle}
                  className={colonne.numerique === true ? 'num' : undefined}
                  aria-sort={
                    sensActif === null
                      ? undefined
                      : sensActif === 'asc'
                        ? 'ascending'
                        : 'descending'
                  }
                >
                  {triable ? (
                    <button
                      type="button"
                      className="table__entete-tri"
                      onClick={() => {
                        basculerTri(colonne);
                      }}
                    >
                      {colonne.entete}
                      <span aria-hidden="true">
                        {sensActif === null ? '' : sensActif === 'asc' ? ' ▲' : ' ▼'}
                      </span>
                    </button>
                  ) : (
                    colonne.entete
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {lignesTriees.map((ligne) => (
            <tr key={cleDe(ligne)}>
              {colonnes.map((colonne) => (
                <td
                  key={colonne.cle}
                  className={colonne.numerique === true ? 'num' : undefined}
                >
                  {colonne.rendu(ligne)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
